// ============================================================================
// levelScript.js — the cinematic layer. A level can carry a `script`: an array
// of beats, each firing ONCE when its trigger hits (either `t` seconds into the
// battle or a `kills` threshold). A beat can speak a line (bark), throw a banner,
// ping the minimap, rewrite the orders, pour in a reinforcement/ambush wave, or
// send a warlord onto the field mid-fight. Runs inside the fixed sim tick so it
// stays deterministic and pauses with hit-stop like everything else.
// ============================================================================

import {
  UNIT_FOOTSOLDIER, UNIT_ARCHER, UNIT_CAPTAIN, TEAM_PLAYER, TEAM_MAGMAR, MAP_HALF,
} from '../core/constants.js';
import { CITADEL, WARCAMP } from '../world/battlefield.js';
import { spawnWarlordByName } from '../ai/warlords.js';
import { EV } from '../core/events.js';

const TYPE = { foot: UNIT_FOOTSOLDIER, archer: UNIT_ARCHER, captain: UNIT_CAPTAIN };
const TEAM = { player: TEAM_PLAYER, magmar: TEAM_MAGMAR, enemy: TEAM_MAGMAR };

export function resetLevelScript(world, level) {
  world._script = (level.script || []).map((s) => ({ ...s, _fired: false }));
  world.events.emit(EV.BATTLE_START, {});
}

export function updateLevelScript(world) {
  const script = world._script;
  if (!script || !script.length || world.gameOver) return;
  for (let i = 0; i < script.length; i++) {
    const s = script[i];
    if (s._fired) continue;
    const byTime = s.t != null && world.levelTime >= s.t;
    const byKills = s.kills != null && world.killCount >= s.kills;
    if (byTime || byKills) { s._fired = true; fire(world, s); }
  }
}

function fire(world, s) {
  // a warlord entrance carries its own taunt (handled in doWarlord); otherwise say here
  if (s.say && !s.warlord) world.events.emit(EV.BARK, { who: s.say.who, line: s.say.line, name: s.say.name, tone: s.say.tone });
  if (s.announce) world.events.emit(EV.ANNOUNCE, { text: s.announce.text, tone: s.announce.tone || 'neutral' });
  if (s.objective) world.events.emit(EV.OBJECTIVE, { text: s.objective.text });
  if (s.ping) { const c = posFor(world, s.ping.at || s.ping); world.events.emit(EV.PING, { x: c.x, z: c.z, tone: s.ping.tone || 'neutral' }); }
  if (s.spawn) doSpawn(world, s.spawn);
  if (s.warlord) doWarlord(world, s);
}

// resolve a named/coord spawn anchor to a { x, z, r } region
function posFor(world, at) {
  const p = world.player;
  if (at && typeof at === 'object' && at.x != null) return { x: at.x, z: at.z, r: at.r || 14 };
  switch (at) {
    case 'north':  return { x: 0, z: CITADEL.z + 30, r: 34 };          // the enemy citadel side
    case 'south':  return { x: WARCAMP.x, z: WARCAMP.z - 24, r: 30 };  // the war-camp
    case 'center': return { x: 0, z: 0, r: 32 };
    case 'east':   return { x: 150, z: 0, r: 30 };
    case 'west':   return { x: -150, z: 0, r: 30 };
    case 'flank': case 'behind': {                                     // an ambush at Benjen's back
      const bx = p.x - Math.sin(p.heading) * 44, bz = p.z - Math.cos(p.heading) * 44;
      return { x: bx, z: bz, r: 12 };
    }
    default:       return { x: p.x, z: p.z, r: 46 };                   // 'around' the player
  }
}

function clampField(v) { const lim = MAP_HALF - 4; return Math.max(-lim, Math.min(lim, v)); }

function doSpawn(world, sp) {
  const team = TEAM[sp.team] != null ? TEAM[sp.team] : TEAM_MAGMAR;
  const type = TYPE[sp.type] != null ? TYPE[sp.type] : UNIT_FOOTSOLDIER;
  const c = posFor(world, sp.at);
  const rMin = c.r > 20 ? c.r * 0.5 : 2;
  const n = Math.max(1, sp.count || 12);
  for (let i = 0; i < n; i++) {
    const a = world.rng() * Math.PI * 2;
    const d = rMin + world.rng() * (c.r - rMin);
    world.spawnUnit(team, type, clampField(c.x + Math.cos(a) * d), clampField(c.z + Math.sin(a) * d));
  }
  if (sp.at === 'flank' || sp.at === 'behind') world.events.emit(EV.PING, { x: c.x, z: c.z, tone: 'bad' });
}

function doWarlord(world, s) {
  const pos = s.at ? posFor(world, s.at) : null;
  const u = spawnWarlordByName(world, s.warlord, pos ? { x: clampField(pos.x), z: clampField(pos.z) } : null);
  // Fire the entrance fanfare even if the unit pool was full, so the beat is never
  // silently dropped — the banner + line + ping still land.
  world.events.emit(EV.ANNOUNCE, { text: `${s.warlord.toUpperCase()} TAKES THE FIELD`, tone: 'bad' });
  if (s.say) world.events.emit(EV.BARK, { who: s.say.who || 'MAGMAR', name: s.warlord, line: s.say.line, tone: 'bad' });
  const pc = u ? { x: u.x, z: u.z } : (pos || posFor(world, 'north'));
  world.events.emit(EV.PING, { x: pc.x, z: pc.z, tone: 'bad' });
}
